import { useState } from 'react';
import { PaylinkStatus } from 'skipify-types';
import { useCreateTransactionMutation } from '../store/modules/mit/api';
import { CreateTransactionBody } from '../store/modules/mit/types';
import { useLazyGetAllPaylinksQuery } from '../store/modules/paylink/api';
import { toast } from '../util/toast';

export type CreateTransactionArgs = {
  customerId: string;
  amount: number;
  merchantReference: string;
  description?: string;
  customerChargeAgreement?: boolean;
};

export const useCreateTransaction = ({ merchantId }: { merchantId: string }) => {
  const [createTransactionFn] = useCreateTransactionMutation();
  const [getPaylinkFn] = useLazyGetAllPaylinksQuery();
  const [isLoading, setIsLoading] = useState(false);
  const createTransaction = async (body: CreateTransactionArgs) => {
    setIsLoading(true);
    const { customerId, amount, merchantReference, description, customerChargeAgreement } = body;

    try {
      // We need to get a paylinkId based on the customerId
      // For that, we're going to get the most recent recurring paylink that has been paid
      const getRecurringResponse = await getPaylinkFn({
        merchantId,
        customerIds: [customerId],
        orderBy: 'desc',
        sortBy: 'createdAt',
        status: [
          PaylinkStatus.PAID,
          PaylinkStatus.PARTIALLY_REFUNDED,
          PaylinkStatus.REFUNDED,
          PaylinkStatus.PARTIALLY_PAID,
        ],
        enableRecurring: true,
        pageSize: 1,
        pageNumber: 1,
      }).unwrap();

      const mostRecentRecurringPaylink = getRecurringResponse?.data?.[0];
      if (!mostRecentRecurringPaylink) {
        throw new Error('No recurring Paylink found for the customer');
      }

      const createTransactionBody: CreateTransactionBody = {
        paylinkId: mostRecentRecurringPaylink.paylinkId,
        amount,
        merchantReference,
        description,
        customerChargeAgreement,
      };

      await createTransactionFn({
        merchantId,
        ...createTransactionBody,
      }).unwrap();

      toast.success('Transaction created successfully');
    } catch (error) {
      let errorMessage = 'Something went wrong while creating the transaction';
      if (error instanceof Error) {
        errorMessage = error.message;
      }
      toast.error(errorMessage);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  return { createTransaction, isLoading };
};
